import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { Fzf, FzfResultItem } from "fzf";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BoardApi } from "../../../services/board-service-api";
import KuikaOverflowText from "../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { buildBoardPath } from "./board-routing";
import { BoardService } from "./board-service";
import { currentUserId } from "./current-user";
import { allowedTargetStatusIds } from "./transitions";
import "./command-palette.scss";
import { Board, BoardStatus, IssueCard, Project, boardProjectKeys } from "./types";
import { useBoardConfig } from "./use-board";

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

type Group = "command" | "board" | "issue";

interface PaletteItem {
  id: string;
  group: Group;
  haystack: string;
  primary: string;
  secondary: string;
  kbd?: string;
  iconNode: React.ReactNode;
  perform: () => void;
}

const GROUP_LABELS: Record<Group, string> = {
  command: "Actions",
  board: "Boards",
  issue: "Issues"
};

const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const { config } = useBoardConfig();
  const navigate = useNavigate();
  const { domainName, issueKey } = useParams<{ domainName: string; issueKey: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState<string>("");
  const [activeIndex, setActiveIndex] = useState<number>(0);
  // Issue results come from the server (no client-held snapshot to scan).
  const [issueCards, setIssueCards] = useState<IssueCard[]>([]);
  // The currently-open issue (route :issueKey), fetched on demand for contextual actions.
  const [openCard, setOpenCard] = useState<IssueCard | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // The currently-active row, captured by a callback ref so we can scroll it into view without
  // querying the DOM by class.
  const activeRowRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActiveIndex(0);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  // Debounced server-side issue search (replaces scanning a client snapshot). Empty query
  // clears results; only commands/boards show until the user types.
  useEffect(() => {
    if (!open) return;
    const trimmed: string = query.trim();
    if (!trimmed) {
      setIssueCards([]);
      return;
    }
    let cancelled: boolean = false;
    const timer: number = window.setTimeout(() => {
      void (async () => {
        const results: IssueCard[] = await BoardApi.searchIssues(trimmed, 25);
        if (!cancelled) setIssueCards(results);
      })();
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, open]);

  // Fetch the open issue (by key) for contextual actions; IssueCard carries everything
  // those actions need (status/labels/assignee/watchers).
  useEffect(() => {
    if (!open || !issueKey) {
      setOpenCard(null);
      return;
    }
    let cancelled: boolean = false;
    void (async () => {
      const matches: IssueCard[] = await BoardApi.searchIssues(issueKey, 5);
      const exact: IssueCard | undefined = matches.find(
        (c: IssueCard) => c.key.toLowerCase() === issueKey.toLowerCase()
      );
      if (!cancelled) setOpenCard(exact ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, issueKey]);

  const staticItems: PaletteItem[] = useMemo(() => {
    const list: PaletteItem[] = [];

    // Commands
    list.push({
      id: "cmd:new-issue",
      group: "command",
      primary: "Create new issue",
      secondary: "Open inline create in the current column",
      haystack: "create new issue add ticket task",
      kbd: "c",
      iconNode: <KuikaSvg name="plus" width={14} height={14} />,
      perform: () => {
        const event: KeyboardEvent = new KeyboardEvent("keydown", { key: "c", bubbles: true });
        window.dispatchEvent(event);
      }
    });
    list.push({
      id: "cmd:shortcuts",
      group: "command",
      primary: "Show keyboard shortcuts",
      secondary: "Open the shortcut cheatsheet",
      haystack: "shortcuts keyboard help hotkey",
      kbd: "?",
      iconNode: <KuikaSvg name="keyboard" width={14} height={14} />,
      perform: () => {
        const event: KeyboardEvent = new KeyboardEvent("keydown", { key: "?", bubbles: true });
        window.dispatchEvent(event);
      }
    });
    const setParam = (key: string, value: string | null): void => {
      const sp: URLSearchParams = new URLSearchParams(searchParams);
      if (value === null) sp.delete(key);
      else sp.set(key, value);
      setSearchParams(sp, { replace: true });
    };

    // Switch view
    const views: { id: string; label: string }[] = [
      { id: "board", label: "Board" },
      { id: "list", label: "List" },
      { id: "backlog", label: "Backlog" },
      { id: "roadmap", label: "Roadmap" },
      { id: "reports", label: "Reports" }
    ];
    for (const v of views) {
      list.push({
        id: `cmd:view:${v.id}`,
        group: "command",
        primary: `View: ${v.label}`,
        secondary: "Switch the current board view",
        haystack: `view switch ${v.label}`,
        iconNode: <KuikaSvg name="layout-board" width={14} height={14} />,
        perform: () => setParam("view", v.id === "board" ? null : v.id)
      });
    }

    // Group by
    const groups: { id: string; label: string }[] = [
      { id: "none", label: "No grouping" },
      { id: "assignee", label: "Assignee" },
      { id: "priority", label: "Priority" },
      { id: "epic", label: "Epic" }
    ];
    for (const g of groups) {
      list.push({
        id: `cmd:group:${g.id}`,
        group: "command",
        primary: `Group by: ${g.label}`,
        secondary: "Change board grouping",
        haystack: `group by ${g.label}`,
        iconNode: <KuikaSvg name="menu-2" width={14} height={14} />,
        perform: () => setParam("groupBy", g.id === "none" ? null : g.id)
      });
    }

    // Quick filters
    list.push({
      id: "cmd:filter:me",
      group: "command",
      primary: "Filter: assigned to me",
      secondary: "Show only your issues",
      haystack: "filter assigned to me my issues",
      iconNode: <KuikaSvg name="user" width={14} height={14} />,
      perform: () => setParam("assignee", currentUserId())
    });
    list.push({
      id: "cmd:filter:due-soon",
      group: "command",
      primary: "Filter: due soon",
      secondary: "Issues due within the next 7 days",
      haystack: "filter due soon this week deadline",
      iconNode: <KuikaSvg name="calendar-event" width={14} height={14} />,
      perform: () => setParam("jql", "due <= 7d")
    });
    list.push({
      id: "cmd:filter:stale",
      group: "command",
      primary: "Filter: stale (> 14d)",
      secondary: "Not updated in over two weeks",
      haystack: "filter stale old untouched not updated",
      iconNode: <KuikaSvg name="calendar-time" width={14} height={14} />,
      perform: () => setParam("jql", "updated <= -14d")
    });
    list.push({
      id: "cmd:filter:clear",
      group: "command",
      primary: "Clear all filters",
      secondary: "Remove assignee / priority / type / JQL filters",
      haystack: "clear filters reset filter",
      iconNode: <KuikaSvg name="x" width={14} height={14} />,
      perform: () => {
        const sp: URLSearchParams = new URLSearchParams(searchParams);
        for (const key of ["assignee", "priority", "type", "jql", "groupBy"]) sp.delete(key);
        setSearchParams(sp, { replace: true });
      }
    });

    // Contextual actions for the currently open issue (route has :issueKey)
    if (openCard) {
      const target: IssueCard = openCard;
      if (target.assigneeUserId !== currentUserId()) {
        list.push({
          id: "issue:assign-me",
          group: "command",
          primary: `Assign ${target.key} to me`,
          secondary: "Set the assignee to you",
          haystack: `assign ${target.key} to me current issue`,
          iconNode: <KuikaSvg name="user" width={14} height={14} />,
          perform: () => BoardService.updateIssue(target.id, { assigneeUserId: currentUserId() })
        });
      }
      const watching: boolean = target.watcherUserIds.includes(currentUserId());
      list.push({
        id: "issue:watch",
        group: "command",
        primary: watching ? `Unwatch ${target.key}` : `Watch ${target.key}`,
        secondary: watching ? "Stop receiving updates" : "Get notified about updates",
        haystack: `watch unwatch ${target.key} subscribe current issue`,
        iconNode: <KuikaSvg name="bell" width={14} height={14} />,
        perform: () => BoardService.toggleWatch(target.id)
      });
      const moveTargets: Set<string> | null = allowedTargetStatusIds(
        config.projects.find((p: Project) => p.key === target.projectKey),
        target.statusId
      );
      for (const status of config.statuses.filter((entry: BoardStatus) => entry.projectKey === target.projectKey)) {
        if (status.id === target.statusId) continue;
        if (moveTargets !== null && !moveTargets.has(status.id)) continue;
        list.push({
          id: `issue:move:${status.id}`,
          group: "command",
          primary: `Move ${target.key} to ${status.name}`,
          secondary: "Transition the current issue",
          haystack: `move transition ${target.key} ${status.name} status`,
          iconNode: <KuikaSvg name="lightning" width={14} height={14} />,
          perform: () =>
            BoardService.moveIssue({
              issueId: target.id,
              destinationStatusId: status.id,
              destinationIndex: 0,
              sourceStatusId: target.statusId
            })
        });
      }
      for (const label of config.labels) {
        if (target.labels.includes(label.name)) continue;
        list.push({
          id: `issue:label:${label.name}`,
          group: "command",
          primary: `Add label "${label.name}" to ${target.key}`,
          secondary: "Tag the current issue",
          haystack: `add label tag ${label.name} ${target.key}`,
          iconNode: <KuikaSvg name="tag" width={14} height={14} />,
          perform: () => BoardService.updateIssue(target.id, { labels: [...target.labels, label.name] })
        });
      }
    }

    // Boards
    for (const board of config.boards) {
      const keys: string[] = boardProjectKeys(board);
      list.push({
        id: `board:${board.id}`,
        group: "board",
        primary: board.name,
        secondary:
          board.scope.kind === "project"
            ? `Project board · ${board.scope.projectKey}`
            : `Multi-project · ${keys.length} projects`,
        haystack: `${board.name} ${keys.join(" ")} board`,
        iconNode: (
          <span
            className="kuika-board-palette__group-dot"
            style={{
              background:
                board.scope.kind === "multi_project"
                  ? "linear-gradient(135deg, var(--blue-5) 0%, var(--purple-5) 100%)"
                  : (config.projects.find((entry: Project) => entry.key === keys[0])?.color ?? "var(--dark-4)")
            }}
          />
        ),
        perform: () => navigate(buildBoardPath(domainName ?? "", board))
      });
    }

    return list;
    // Issues are NOT built here any more — they come from the server search (issueItems below).
  }, [config, openCard, domainName, navigate, searchParams, setSearchParams]);

  // Issue results from the server search, as palette items (navigates to the issue's board).
  const issueItems: PaletteItem[] = useMemo(() => {
    return issueCards.map((card: IssueCard) => ({
      id: `issue:${card.id}`,
      group: "issue" as Group,
      primary: card.summary,
      secondary: `${card.key} · ${card.labels.join(", ")}`.replace(/ · $/, ""),
      haystack: `${card.key} ${card.summary}`,
      kbd: card.key,
      iconNode: (
        <span
          className={`kuika-board-palette__issue-type kuika-board-palette__issue-type--${card.type}`}
          aria-hidden="true"
        />
      ),
      perform: () => {
        const board: Board | undefined = config.boards.find(
          (entry: Board) => entry.scope.kind === "project" && entry.scope.projectKey === card.projectKey
        );
        if (board) {
          navigate(`${buildBoardPath(domainName ?? "", board)}/${card.key}`);
        } else {
          navigate(`/${domainName}/issue/${card.key}`);
        }
      }
    }));
  }, [issueCards, config.boards, domainName, navigate]);

  const filtered: PaletteItem[] = useMemo(() => {
    const trimmed: string = query.trim();
    if (!trimmed) {
      return staticItems.slice(0, 30);
    }
    // Commands + boards are bounded → fuzzy-match client-side. Issues come pre-matched from
    // the server (issueItems) and are appended after.
    const fzf = new Fzf(staticItems, {
      selector: (item: PaletteItem) => item.haystack,
      tiebreakers: [
        (a: FzfResultItem<PaletteItem>, b: FzfResultItem<PaletteItem>) => {
          const order: Record<Group, number> = { command: 0, board: 1, issue: 2 };
          return order[a.item.group] - order[b.item.group];
        }
      ]
    });
    const matchedStatic: PaletteItem[] = fzf.find(trimmed).map((entry: FzfResultItem<PaletteItem>) => entry.item);
    return [...matchedStatic, ...issueItems].slice(0, 60);
  }, [staticItems, issueItems, query]);

  useEffect(() => {
    if (activeIndex >= filtered.length) setActiveIndex(Math.max(0, filtered.length - 1));
  }, [filtered, activeIndex]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === "ArrowDown" || (event.key === "n" && event.ctrlKey)) {
        event.preventDefault();
        setActiveIndex((prev: number) => Math.min(filtered.length - 1, prev + 1));
        return;
      }
      if (event.key === "ArrowUp" || (event.key === "p" && event.ctrlKey)) {
        event.preventDefault();
        setActiveIndex((prev: number) => Math.max(0, prev - 1));
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        const target: PaletteItem | undefined = filtered[activeIndex];
        if (target) {
          onClose();
          target.perform();
        }
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, filtered, activeIndex, onClose]);

  // Scroll active item into view
  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, filtered]);

  const grouped: Array<{ group: Group; items: PaletteItem[] }> = (["command", "board", "issue"] as Group[])
    .map((group: Group) => ({ group, items: filtered.filter((entry: PaletteItem) => entry.group === group) }))
    .filter((entry) => entry.items.length > 0);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="kuika-board-palette__backdrop"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          data-testid="board-command-palette"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12 }}
        >
          <motion.div
            className="kuika-board-palette"
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
            initial={{ opacity: 0, scale: 0.97, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: -8 }}
            transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="kuika-board-palette__input-row">
              <KuikaSvg name="search" width={16} height={16} />
              <input
                ref={inputRef}
                className="kuika-board-palette__input"
                placeholder="Search issues, boards, actions…"
                value={query}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
                autoFocus
              />
              <kbd className="kuika-board-palette__hint">esc</kbd>
            </div>
            <div className="kuika-board-palette__list">
              {grouped.length === 0 && <div className="kuika-board-palette__empty">No matches.</div>}
              {grouped.map(({ group, items: groupItems }) => (
                <div key={group} className="kuika-board-palette__group">
                  <div className="kuika-board-palette__group-title">{GROUP_LABELS[group]}</div>
                  {groupItems.map((item: PaletteItem) => {
                    const indexInFiltered: number = filtered.indexOf(item);
                    const isActive: boolean = indexInFiltered === activeIndex;
                    return (
                      <button
                        key={item.id}
                        ref={isActive ? activeRowRef : undefined}
                        type="button"
                        className={classNames("kuika-board-palette__row", OVERFLOW_REVEAL_HOST_CLASS, {
                          "kuika-board-palette__row--active": isActive
                        })}
                        onMouseEnter={() => setActiveIndex(indexInFiltered)}
                        onClick={() => {
                          onClose();
                          item.perform();
                        }}
                      >
                        <span className="kuika-board-palette__row-icon">{item.iconNode}</span>
                        <span className="kuika-board-palette__row-main">
                          <span className="kuika-board-palette__row-primary">
                            <KuikaOverflowText>{item.primary}</KuikaOverflowText>
                          </span>
                          {item.secondary && (
                            <span className="kuika-board-palette__row-secondary">
                              <KuikaOverflowText>{item.secondary}</KuikaOverflowText>
                            </span>
                          )}
                        </span>
                        {item.kbd && <span className="kuika-board-palette__row-kbd">{item.kbd}</span>}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="kuika-board-palette__footer">
              <span>
                <kbd>↑↓</kbd> navigate
              </span>
              <span>
                <kbd>↵</kbd> open
              </span>
              <span>
                <kbd>esc</kbd> close
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette;
